import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface MedicalHistoryProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Injury Information
  date_of_injury: string;
  body_parts_injured: string;

  // Healthcare Providers
  current_pcp: string;
  specialist: string;
  additional_treating_providers: string;
  psychiatrist_psychologist_counselor: string;
  pain_management_physician: string;

  // Treatment
  treatment: string;

  // Aides for Independent Living
  cane: boolean;
  walker: boolean;
  wheelchair: boolean;
  shower_chair: boolean;
  hand_held_shower_hose: boolean;
  grab_bars: boolean;
  back_brace: boolean;
  hand_wrist_brace: boolean;
  other_aides: boolean;
  other_aides_specify: string;

  // Medications
  is_taking_medications: string;

  // Medication 1
  medication1_name: string;
  medication1_dosage: string;
  medication1_prescribed_times: string;

  // Medication 2
  medication2_name: string;
  medication2_dosage: string;
  medication2_prescribed_times: string;

  // Medication 3
  medication3_name: string;
  medication3_dosage: string;
  medication3_prescribed_times: string;

  // Medication 4
  medication4_name: string;
  medication4_dosage: string;
  medication4_prescribed_times: string;

  // Medication 5
  medication5_name: string;
  medication5_dosage: string;
  medication5_prescribed_times: string;

  // Medication 6
  medication6_name: string;
  medication6_dosage: string;
  medication6_prescribed_times: string;
}

const MedicalHistory: React.FC<MedicalHistoryProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'medical_history', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'medical_history', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Injury Information
    date_of_injury: '',
    body_parts_injured: '',

    // Healthcare Providers
    current_pcp: '',
    specialist: '',
    additional_treating_providers: '',
    psychiatrist_psychologist_counselor: '',
    pain_management_physician: '',

    // Treatment
    treatment: '',

    // Aides for Independent Living
    cane: false,
    walker: false,
    wheelchair: false,
    shower_chair: false,
    hand_held_shower_hose: false,
    grab_bars: false,
    back_brace: false,
    hand_wrist_brace: false,
    other_aides: false,
    other_aides_specify: '',

    // Medications
    is_taking_medications: '',

    // Medication 1
    medication1_name: '',
    medication1_dosage: '',
    medication1_prescribed_times: '',

    // Medication 2
    medication2_name: '',
    medication2_dosage: '',
    medication2_prescribed_times: '',

    // Medication 3
    medication3_name: '',
    medication3_dosage: '',
    medication3_prescribed_times: '',

    // Medication 4
    medication4_name: '',
    medication4_dosage: '',
    medication4_prescribed_times: '',

    // Medication 5
    medication5_name: '',
    medication5_dosage: '',
    medication5_prescribed_times: '',

    // Medication 6
    medication6_name: '',
    medication6_dosage: '',
    medication6_prescribed_times: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderMedicationFields = (medicationNumber: number) => (
    <Box
      sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}
    >
      <Typography variant='subtitle1' gutterBottom>
        Medication {medicationNumber}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label='Medication'
          value={
            formData[`medication${medicationNumber}_name` as keyof FormData]
          }
          onChange={e =>
            handleChange(`medication${medicationNumber}_name`, e.target.value)
          }
          fullWidth
        />
        <TextField
          label='Dosage'
          value={
            formData[`medication${medicationNumber}_dosage` as keyof FormData]
          }
          onChange={e =>
            handleChange(`medication${medicationNumber}_dosage`, e.target.value)
          }
          fullWidth
        />
        <TextField
          label='Prescribed times of the Day'
          value={
            formData[
              `medication${medicationNumber}_prescribed_times` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `medication${medicationNumber}_prescribed_times`,
              e.target.value
            )
          }
          fullWidth
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Medical History Information
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Injury Information */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Injury Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label='Date of the Injury'
              type='date'
              value={formData.date_of_injury}
              onChange={e => handleChange('date_of_injury', e.target.value)}
              required={isFieldRequiredLocal('date_of_injury')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label='Which Parts of the body were injured?'
              multiline
              rows={3}
              value={formData.body_parts_injured}
              onChange={e => handleChange('body_parts_injured', e.target.value)}
              fullWidth
            />
          </Box>
        </Box>

        {/* Healthcare Providers */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Healthcare Providers
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label='Name of the Current/Last PCP'
              value={formData.current_pcp}
              onChange={e => handleChange('current_pcp', e.target.value)}
              fullWidth
            />
            <TextField
              label='Specialist (Eg: Neurologist, Orthopedist)'
              value={formData.specialist}
              onChange={e => handleChange('specialist', e.target.value)}
              fullWidth
            />
            <TextField
              label='Additional treating providers'
              multiline
              rows={2}
              value={formData.additional_treating_providers}
              onChange={e =>
                handleChange('additional_treating_providers', e.target.value)
              }
              fullWidth
            />
            <TextField
              label='Name of Psychiatrist, Psychologist, and or Counselor'
              value={formData.psychiatrist_psychologist_counselor}
              onChange={e =>
                handleChange(
                  'psychiatrist_psychologist_counselor',
                  e.target.value
                )
              }
              fullWidth
            />
            <TextField
              label='Pain Management Physician'
              value={formData.pain_management_physician}
              onChange={e =>
                handleChange('pain_management_physician', e.target.value)
              }
              fullWidth
            />
          </Box>
        </Box>

        {/* Treatment */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Treatment
          </Typography>
          <TextField
            label='Treatment'
            multiline
            rows={3}
            value={formData.treatment}
            onChange={e => handleChange('treatment', e.target.value)}
            fullWidth
          />
        </Box>

        {/* Aides for Independent Living */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Aides for Independent Living
          </Typography>
          <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 2 }}>
            (Select all Applicable, Other Please Specify)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.cane}
                  onChange={e => handleChange('cane', e.target.checked)}
                />
              }
              label='Cane'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.walker}
                  onChange={e => handleChange('walker', e.target.checked)}
                />
              }
              label='Walker'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.wheelchair}
                  onChange={e => handleChange('wheelchair', e.target.checked)}
                />
              }
              label='Wheelchair'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.shower_chair}
                  onChange={e => handleChange('shower_chair', e.target.checked)}
                />
              }
              label='Shower Chair'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.hand_held_shower_hose}
                  onChange={e =>
                    handleChange('hand_held_shower_hose', e.target.checked)
                  }
                />
              }
              label='Hand Held Shower Hose'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.grab_bars}
                  onChange={e => handleChange('grab_bars', e.target.checked)}
                />
              }
              label='Grab Bars'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.back_brace}
                  onChange={e => handleChange('back_brace', e.target.checked)}
                />
              }
              label='Back Brace'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.hand_wrist_brace}
                  onChange={e =>
                    handleChange('hand_wrist_brace', e.target.checked)
                  }
                />
              }
              label='Hand/Wrist Brace'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.other_aides}
                  onChange={e => handleChange('other_aides', e.target.checked)}
                />
              }
              label='Other (Please Specify)'
            />
            {formData.other_aides && (
              <TextField
                label='Other Aides Specify'
                value={formData.other_aides_specify}
                onChange={e =>
                  handleChange('other_aides_specify', e.target.value)
                }
                fullWidth
                sx={{ ml: 4 }}
              />
            )}
          </Box>
        </Box>

        {/* Medications */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Medications
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Are you currently taking any medications?
            </Typography>
            <RadioGroup
              value={formData.is_taking_medications}
              onChange={e =>
                handleChange('is_taking_medications', e.target.value)
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>

          {formData.is_taking_medications === 'yes' && (
            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Medications
              </Typography>

              {renderMedicationFields(1)}
              {renderMedicationFields(2)}
              {renderMedicationFields(3)}
              {renderMedicationFields(4)}
              {renderMedicationFields(5)}
              {renderMedicationFields(6)}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MedicalHistory;
